import { useState } from "react";
import { Alert } from "@/components/Alert";
import { ModalDelete } from "@/components/ModalDelete";
import { Textarea } from "@/components/Textarea";
import {
    ProviderApiError,
    useProviderApi,
    useProviderContext,
} from "@/pages/Providers/useProviderApi";
import type { Provider } from "@/pages/Providers/types";

interface ProviderStatusDialogProps {
    provider: Provider;
    onClose: () => void;
    onSaved: (provider: Provider) => void;
}

export const ProviderStatusDialog = ({
    provider,
    onClose,
    onSaved,
}: ProviderStatusDialogProps) => {
    const { t, sendMessage } = useProviderContext();
    const { request } = useProviderApi();
    const [observation, setObservation] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const active = Number(provider.is_active) !== 1;
    const action = active ? "enable" : "disable";
    const confirm = async (): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            const response = await request<{ item: Provider }>(
                `/${provider.id}/status`,
                {
                    active,
                    confirmed: true,
                    observation,
                    version: provider.version,
                }
            );
            sendMessage(
                t(`providers.ui.${active ? "enabled" : "disabled"}`),
                "success"
            );
            onSaved(response.item);
        } catch (failure: unknown) {
            setError(
                failure instanceof ProviderApiError && failure.status === 409
                    ? "providers.errors.stale"
                    : "providers.errors.request"
            );
        } finally {
            setBusy(false);
        }
    };
    return (
        <ModalDelete
            isOpen
            onClose={busy ? () => {} : onClose}
            title={t(`providers.ui.${action}`)}
            itemName={provider.name}
            description={t(`providers.ui.${action}_explanation`)}
            submitLabel={t(`providers.ui.confirm_${action}`)}
            submittingLabel={t("providers.ui.saving")}
            isDeleting={busy}
            onConfirm={confirm}
        >
            {active && provider.conflict_count > 0 && (
                <Alert variant="warning">
                    {t("providers.ui.enable_conflicts", {
                        count: provider.conflict_count,
                    })}
                </Alert>
            )}
            {error && <Alert variant="error">{t(error)}</Alert>}
            <Textarea
                label={t("providers.ui.observation")}
                aria-label={t("providers.ui.observation")}
                value={observation}
                onChange={(event) => setObservation(event.target.value)}
                maxLength={5000}
            />
        </ModalDelete>
    );
};
